import { RoadNodeType as ApiRoadNodeType, RoadNodesApi, RoadSegmentsApi } from '@yard/app/api'
import { createApiClient } from '@yard/app/http-client'
import {
  RoadNodeDto,
  RoadNodeType,
  RoadSegmentDto,
} from '@yard/features/road-network/models/road-network.model'

export interface IRoadNetworkService {
  getNodes(): Promise<RoadNodeDto[]>
  getSegments(): Promise<RoadSegmentDto[]>
  createNode(node: Omit<RoadNodeDto, 'id'>): Promise<RoadNodeDto>
  updateNode(node: RoadNodeDto): Promise<void>
  deleteNode(id: string): Promise<void>
  createSegment(segment: Omit<RoadSegmentDto, 'id'>): Promise<RoadSegmentDto>
  updateSegment(segment: RoadSegmentDto): Promise<void>
  deleteSegment(id: string): Promise<void>
}

export class RoadNetworkService implements IRoadNetworkService {
  private readonly nodesClient = createApiClient(RoadNodesApi)
  private readonly segmentsClient = createApiClient(RoadSegmentsApi)

  async getNodes(type?: RoadNodeType): Promise<RoadNodeDto[]> {
    const { data } = await this.nodesClient.getRoadNodes(type as ApiRoadNodeType | undefined)
    return data.roadNodes
  }

  async getSegments(): Promise<RoadSegmentDto[]> {
    const { data } = await this.segmentsClient.getRoadSegments()
    return data.roadSegments
  }

  async createNode(node: Omit<RoadNodeDto, 'id'>): Promise<RoadNodeDto> {
    const { data } = await this.nodesClient.createRoadNode({
      name: node.name,
      type: node.type as ApiRoadNodeType,
      layoutPosition: node.layoutPosition,
      layoutObjectId: node.layoutObjectId,
      blockAccessPointId: node.blockAccessPointId,
    })
    return { ...node, id: data.id }
  }

  async updateNode(node: RoadNodeDto): Promise<void> {
    await this.nodesClient.updateRoadNode({
      id: node.id,
      name: node.name,
      type: node.type as ApiRoadNodeType,
      layoutPosition: node.layoutPosition,
      layoutObjectId: node.layoutObjectId,
      blockAccessPointId: node.blockAccessPointId,
    })
  }

  async deleteNode(id: string): Promise<void> {
    await this.nodesClient.deleteRoadNode({ id })
  }

  async createSegment(segment: Omit<RoadSegmentDto, 'id'>): Promise<RoadSegmentDto> {
    const { data } = await this.segmentsClient.createRoadSegment({
      startNodeId: segment.startNodeId,
      endNodeId: segment.endNodeId,
      direction: segment.direction,
      isBlocked: segment.isBlocked,
      weightLimit: segment.weightLimit,
    })
    return { ...segment, id: data.id }
  }

  async updateSegment(segment: RoadSegmentDto): Promise<void> {
    await this.segmentsClient.updateRoadSegment({
      id: segment.id,
      startNodeId: segment.startNodeId,
      endNodeId: segment.endNodeId,
      direction: segment.direction,
      isBlocked: segment.isBlocked,
      weightLimit: segment.weightLimit,
    })
  }

  async deleteSegment(id: string): Promise<void> {
    await this.segmentsClient.deleteRoadSegment({ id })
  }
}

export const roadNetworkService = new RoadNetworkService()
